<template>
  <v-container fluid>
    <MiniAlerts style="white-space: pre-line" v-if="alert.showAlert" :message="alert.message" />
    <validation-observer ref="form" v-slot="{ invalid }">
      <v-form @submit.prevent="createPD" :disabled="disabledDiary">
        <v-row v-if="approvedDiary">
          <v-col cols="12">
            <v-alert text outlined type="success">
              Esta diária está <strong>aprovada!</strong>
            </v-alert>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="2">
            <v-text-field
              ref="dateInput"
              v-model="fields.Data"
              type="date"
              id="birthday"
              :label="method == 'create' ? 'Insira data' : 'Data'"
              dense
              outlined
              :max="maxDate"
              :disabled="method == 'update' && (!canRecode || approvedDiary)"
              @click="openNativeDatePicker"
              @focus="openNativeDatePicker"
              @mousedown.prevent="openNativeDatePicker"
              @change="recodeDateChanged"
            />
          </v-col>

          <v-col cols="12" md="4" v-if="fields.Data != null">
            <validation-provider v-slot="{ errors }" vid="Obras" name="Obras" rules="required">
              <v-autocomplete
                :disabled="method == 'update'"
                v-model="fields.obra_id"
                :items="obras"
                outlined
                dense
                :item-text="item => item.Codigo + ' - ' + item.Descricao"
                item-value="Id"
                :error-messages="errors"
                label="Obras *"
                @change="changeWork"
              />
            </validation-provider>
          </v-col>

          <v-col cols="12" md="3" v-if="fields.obra_id != null && frentes_obra.length > 0 && hideFrentes == false">
            <validation-provider v-slot="{ errors }" vid="Frentes" name="Frentes" rules="required">
              <v-autocomplete
                :disabled="method == 'update' && (!canRecode || approvedDiary)"
                v-model="fields.frente_id"
                :items="frentes_obra"
                outlined
                dense
                :item-text="ob => getText(ob)"
                item-value="ID"
                :error-messages="errors"
                label="Frentes de Obra *"
                @change="changeFrenteObra"
              />
            </validation-provider>
          </v-col>

          <v-col cols="12" md="3" v-if="classeNeeded">
            <validation-provider v-slot="{ errors }" vid="Classes" name="Classes" rules="required">
              <v-autocomplete
                :disabled="method == 'update' && (!canRecode || approvedDiary)"
                v-model="fields.classe_id"
                :items="classes"
                outlined
                dense
                :item-text="item => item.Descricao"
                item-value="ID"
                :error-messages="errors"
                label="Classe *"
                @change="changeClasse"
              />
            </validation-provider>
          </v-col>

          <v-col cols="12" md="2" v-if="weatherSummary">
            <div class="d-flex align-center">
              <v-icon large class="mr-2">
                {{ weatherSummary.icon }}
              </v-icon>

              <div class="text-right">
                <div class="caption font-weight-bold">
                  {{ weatherSummary.conditionText }}
                </div>
                <div class="caption">
                  Vento: {{ weatherSummary.wind }} km/h
                </div>
                <div class="caption">
                  Máx: {{ weatherSummary.max }}°C · Min: {{ weatherSummary.min }}°C
                </div>
              </div>
            </div>
          </v-col>
        </v-row>

        <v-row v-if="showContent">
          <v-col cols="12" class="notranslate" translate="no">
            Máquina: <v-icon color="success">mdi-bulldozer</v-icon>
            Acessório: <v-icon color="warning">mdi-tag</v-icon>
          </v-col>

          <v-col cols="12" class="ma-0 pa-0">
            <v-simple-table class="d-none d-lg-block elevation-5">
              <template v-slot:default>
                <thead class="primary">
                  <tr>
                    <th width="15%" class="text-center white--text text-caption font-weight-bold">Equipamento</th>
                    <th translate="no" class="nostranslate text-center white--text text-caption font-weight-bold">HS</th>
                    <th class="text-center white--text text-caption font-weight-bold">HO</th>
                    <th class="text-center white--text text-caption font-weight-bold">HA</th>
                    <th class="text-center white--text text-caption font-weight-bold">KMi</th>
                    <th class="text-center white--text text-caption font-weight-bold">KM</th>
                    <th class="text-center white--text text-caption font-weight-bold">Hi</th>
                    <th class="text-center white--text text-caption font-weight-bold">H</th>
                    <th v-if="false" class="text-center white--text text-caption font-weight-bold">Nota</th>
                    <th class="text-center white--text text-caption font-weight-bold">Utilizador</th>
                    <th class="text-center white--text text-caption font-weight-bold">HN</th>
                    <th class="text-center white--text text-caption font-weight-bold">HExt</th>
                    <th class="text-center white--text text-caption font-weight-bold">Diesel</th>
                    <th class="text-center white--text text-caption font-weight-bold">Óleo</th>
                    <th class="text-center white--text text-caption font-weight-bold">Adblue</th>
                    <th></th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="(item, index) in getLines"
                    v-bind:key="item.id"
                    :class="{ 'bg-gray-100': index % 2 !== 0 }"
                  >
                    <td>
                      <validation-provider v-slot="{ errors }" vid="Equipamento" name="Equipamento" rules="required">
                        <div v-if="checkNew(item)">
                          <v-autocomplete
                            class="mt-4"
                            dense
                            outlined
                            v-if="item.accessory == false"
                            :disabled="disableBox(item)"
                            v-model="item.equip"
                            :items="getEquipamentos"
                            hide-selected
                            :error-messages="errors"
                            item-value="Id"
                            :item-text="item => item.EntityKey + ' - ' + item.Descricao"
                            label="Equipamento"
                          ></v-autocomplete>

                          <v-autocomplete
                            v-else
                            class="mt-4"
                            dense
                            outlined
                            v-model="item.equip"
                            :disabled="disableBox(item)"
                            :items="unassignedMachines"
                            item-value="Id"
                            hide-selected
                            :error-messages="errors"
                            :item-text="item => item.EntityKey + ' - ' + item.Descricao"
                            label="Acessório"
                          />
                        </div>

                        <div v-else class="text-left">
                          <span class="text-caption">
                            <v-icon color="warning" v-if="item.accessory">mdi-tag</v-icon>
                            <v-icon color="success" v-else>mdi-bulldozer</v-icon>
                            {{getVehicleName(item)}}
                          </span>
                        </div>
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="HS" name="HS" rules="required">
                        <v-text-field
                          class="mt-4 notranslate"
                          translate="no"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="HS"
                          placeholder="Insira HS"
                          v-model="item.hs"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="HO" name="HO" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="HO"
                          placeholder="Insira HO"
                          v-model="item.ho"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="HA" name="HA" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="HA"
                          placeholder="Insira HA"
                          v-model="item.ha"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="KMi" name="KMi" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="KMi"
                          placeholder="Insira KMi"
                          v-model="item.kmi"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="KM" name="KM" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="KM"
                          placeholder="Insira KM"
                          v-model="item.km"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="Hi" name="Hi" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="Hi"
                          placeholder="Insira H inicial"
                          v-model="item.hi"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="H" name="H" rules="required">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="H"
                          placeholder="Insira H"
                          v-model="item.h"
                        />
                      </validation-provider>
                    </td>

                    <td v-if="false">
                      <validation-provider v-slot="{ errors }" vid="Local" name="Local" rules="|">
                        <v-text-field
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="Nota"
                          placeholder="Insira nota"
                          v-model="item.local"
                          @click="openEditorModal(item)"
                        />
                      </validation-provider>
                    </td>

                    <td class="text-center">
                      <Tooltip v-if="item.CodigoMO != null" color="warning" :text="item.name" top>
                        <span class="font-weight-bold text-center">{{ item.CodigoMO == null ? '-' : item.CodigoMO}}</span>
                      </Tooltip>
                      <span class="text-caption text-center" v-else>-</span>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="HN" name="HN" :rules="item.accessory ? '' : 'required'">
                        <v-text-field
                          :disabled="item.accessory"
                          class="mt-4"
                          dense
                          :readonly="approvedDiary"
                          outlined
                          label="HN"
                          placeholder="Insira HN"
                          v-model="item.hn"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" :rules="item.accessory ? '' : 'required'">
                        <v-text-field
                          :disabled="item.accessory"
                          class="mt-4"
                          dense
                          :readonly="approvedDiary"
                          outlined
                          label="HExt"
                          placeholder="Insira HExt"
                          v-model="item.hext"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="Diesel" name="Diesel" :rules="item.accessory ? '' : 'required'">
                        <v-text-field
                          :disabled="item.accessory"
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="Qtd. Diesel"
                          placeholder="Insira Qty"
                          v-model="item.diesel"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="Óleo" name="Óleo" :rules="item.accessory ? '' : 'required'">
                        <v-text-field
                          :disabled="item.accessory"
                          class="mt-4"
                          dense
                          outlined
                          :readonly="approvedDiary"
                          label="Qtd. Óleo"
                          placeholder="Insira Qty"
                          v-model="item.oil"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <validation-provider v-slot="{ errors }" vid="Adblue" name="Adblue" :rules="item.accessory ? '' : 'required'">
                        <v-text-field
                          :disabled="item.accessory"
                          class="mt-4"
                          dense
                          :readonly="approvedDiary"
                          outlined
                          label="Qtd. Adblue"
                          placeholder="Insira Qty"
                          v-model="item.adblue"
                        />
                      </validation-provider>
                    </td>

                    <td>
                      <v-btn
                        v-if="!disabledDiary"
                        v-show="!approvedDiary"
                        dark
                        x-small
                        fab
                        color="error"
                        @click="deleteLine(item)"
                      >
                        <v-icon x-small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-data-iterator
              :items="line"
              item-key="id"
              :items-per-page="-1"
              hide-default-footer
              class="d-block d-lg-none"
            >
              <template v-slot:default="props">
                <v-card v-for="item in props.items" :key="item.id" outlined class="mt-4 elevation-0">
                  <v-card-text>
                    <v-row>
                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Equipamento" name="Equipamento" rules="required">
                          <div v-if="checkNew(item)">
                            <v-autocomplete
                              v-if="item.accessory == false"
                              :disabled="disableBox(item)"
                              v-model="item.equip"
                              dense
                              outlined
                              :items="getEquipamentos"
                              hide-selected
                              :error-messages="errors"
                              item-value="Id"
                              :item-text="item => item.EntityKey + ' - ' + item.Descricao"
                              label="Equipamento"
                            ></v-autocomplete>

                            <v-autocomplete
                              v-else
                              v-model="item.equip"
                              dense
                              outlined
                              :disabled="disableBox(item)"
                              :items="unassignedMachines"
                              item-value="Id"
                              hide-selected
                              :error-messages="errors"
                              :item-text="item => item.EntityKey + ' - ' + item.Descricao"
                              label="Acessório"
                            />
                          </div>

                          <div v-else class="text-center">
                            <v-text-field dense outlined disabled :value="getVehicleName(item)" />
                          </div>
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="HS" name="HS" rules="required">
                          <v-text-field :readonly="approvedDiary" label="HS" placeholder="Insira HS" dense outlined v-model="item.hs" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="HO" name="HO" rules="required">
                          <v-text-field :readonly="approvedDiary" label="HO" placeholder="Insira HO" dense outlined v-model="item.ho" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="HA" name="HA" rules="required">
                          <v-text-field :readonly="approvedDiary" label="HA" placeholder="Insira HA" dense outlined v-model="item.ha" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="KMi" name="KMi" rules="required">
                          <v-text-field :readonly="approvedDiary" label="KMi" placeholder="Insira KMi" dense outlined v-model="item.kmi" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="km" name="km" rules="required">
                          <v-text-field :readonly="approvedDiary" label="KM" placeholder="Insira KM" dense outlined v-model="item.km" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Hi" name="Hi" rules="required">
                          <v-text-field :readonly="approvedDiary" label="Hi" placeholder="Insira H inicial" dense outlined v-model="item.hi" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="H" name="H" rules="required">
                          <v-text-field :readonly="approvedDiary" label="H" placeholder="Insira H" dense outlined v-model="item.h" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12" v-if="false">
                        <validation-provider v-slot="{ errors }" vid="Local" name="Local" rules="|">
                          <v-text-field :readonly="approvedDiary" label="Nota" placeholder="Insira nota" dense outlined v-model="item.local" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Utilizador" name="Utilizador" rules="|">
                          <v-text-field
                            disabled
                            label="Utilizador"
                            dense
                            outlined
                            placeholder="Utilizador"
                            :value="item.CodigoMO == null ? '-' : item.CodigoMO + ' - ' + item.name"
                          />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="HN" name="HN" :rules="item.accessory ? '' : 'required'">
                          <v-text-field :readonly="approvedDiary" label="HN" placeholder="Insira HN" dense outlined v-model="item.hn" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="HExt" name="HExt" :rules="item.accessory ? '' : 'required'">
                          <v-text-field :readonly="approvedDiary" label="HExt" placeholder="Insira HExt" dense outlined v-model="item.hext" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Diesel" name="Diesel" :rules="item.accessory ? '' : 'required'">
                          <v-text-field :readonly="approvedDiary" label="Diesel" placeholder="Insira Diesel" dense outlined v-model="item.diesel" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Óleo" name="Óleo" :rules="item.accessory ? '' : 'required'">
                          <v-text-field :readonly="approvedDiary" label="Óleo" placeholder="Insira óleo" dense outlined v-model="item.oil" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <validation-provider v-slot="{ errors }" vid="Adblue" name="Adblue" :rules="item.accessory ? '' : 'required'">
                          <v-text-field :readonly="approvedDiary" label="Adblue" placeholder="Insira Adblue" dense outlined v-model="item.adblue" />
                        </validation-provider>
                      </v-col>

                      <v-col cols="12">
                        <v-btn
                          v-if="!disabledDiary"
                          v-show="!approvedDiary"
                          dark
                          x-small
                          fab
                          color="error"
                          @click="deleteLine(item)"
                        >
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>
            </v-data-iterator>
          </v-col>
        </v-row>

        <v-row justify="center" v-if="showContent">
          <v-col cols="12" md="auto">
            <v-btn
              v-if="!disabledDiary"
              v-show="!approvedDiary"
              fab
              dark
              small
              color="primary"
              class="mt-4 mb-4"
              @click="addLineToTable"
            >
              <v-icon small>mdi-plus</v-icon>
            </v-btn>
          </v-col>

          <v-col cols="12" md="auto">
            <v-btn
              v-if="!disabledDiary"
              v-show="!approvedDiary"
              fab
              dark
              small
              class="mt-4 mb-4"
              color="warning"
              @click="addAccessoryLine"
            >
              <v-icon small>mdi-tag</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="showContent" class="elevation-5">
          <v-col cols="12" class="primary white--text">
            Trabalhos efetuados / Observações
          </v-col>
          <v-col cols="12">
            <v-textarea :readonly="approvedDiary" v-model="fields.observations" outlined dense />
          </v-col>
        </v-row>

        <v-row v-if="showContent" class="mt-4">
          <v-col cols="12" align="end">
            <v-btn
              v-if="!disabledDiary"
              v-show="!approvedDiary"
              :disabled="checkSaveButton"
              @click="createPD_validator"
              class="success"
              rounded
            >
              Gravar<v-icon class="ml-2">mdi-content-save</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </validation-observer>

    <v-dialog v-if="editorModal" v-model="editorModal" max-width="600px" scrollable>
      <InputEditorModal
        @change-value="changeEditorModal"
        vid="editor_local"
        :title="'Nota'"
        :label="'Nota'"
        v-model="inputEditorModalData.itemModel"
      />
    </v-dialog>

    <ErrorSnackbar v-model="error" :timeout="timeout" :message="error_message" @input="timeout = 2000;" />
    <SuccessSnackbar message="Dados gravados!" v-model="success" />
    <Loader v-if="loader" />

    <ConfirmModal
      @yes="createPD"
      @close-modal="confirmationModal.show = false"
      v-show="confirmationModal.show"
      :title="confirmationModal.title"
      :message="confirmationModal.message"
    />

    <DialogSuccess :opened="savedSuccess" @on-ok="onSuccess">
      Diária de Manobrador gravada com sucesso
    </DialogSuccess>

    <!-- NOVO DIALOG: HExt null (e só HExt null) -->
    <v-dialog v-model="hextDialog.show" max-width="560px" persistent>
      <v-card>
        <v-card-title class="primary white--text text-h6">
          Confirmação
        </v-card-title>

        <v-card-text class="mt-2">
          Campo de <strong>horas extra (HExt)</strong> não preenchido.<br/>Iremos preencher automaticamente o campo com <strong>0 horas</strong>.
          Deseja submeter a diária na mesma?
        </v-card-text>

        <v-card-actions>
          <v-btn color="error" @click="hextDialog.show = false">Não</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="confirmHextAndSubmit">Sim</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import DateInput from "@/components/UI/Inputs/DateInput"
import InputEditorModal from "@/components/UI/Modals/InputEditorModal";
import Obra from "@/api/Obra.js"
import Equipamentos from "@/api/Equipamentos.js"
import FrenteObra from "@/api/FrenteObra.js"
import PDEq from "@/api/PDEq.js"
import Classe from "@/api/Classe.js"
import dayjs from 'dayjs'
import ErrorSnackbar from "@/components/UI/Snackbars/ErrorSnackbar";
import ConfirmModal from '@/components/Modals/ConfirmModal.vue';
import SuccessSnackbar from "@/components/UI/Snackbars/SuccessSnackbar";
import Loader from "@/components/UI/Loaders/Loader";
import MiniAlerts from '@/components/Alerts/MiniAlerts.vue';
import Tooltip from '@/components/UI/Tooltip.vue';
import DialogSuccess from '@/components/UI/DialogSuccess.vue';
import Work from '@/api/Works.js'

export default{
  name:"Operator",
  components:{
    DateInput,
    InputEditorModal,
    ErrorSnackbar,
    ConfirmModal,
    SuccessSnackbar,
    Loader,
    MiniAlerts,
    Tooltip,
    DialogSuccess
  },
  props:{
    method: String,
    diary: Object
  },
  watch: {
    diary: {
      immediate: true,
      handler (val) {
        if(!val) return;

        this.originalCodification = {
          Data: val.Data ?? null,
          frente_id: val.frente_id ?? null,
          classe_id: val.classe_id ?? null
        }

        let data = {...val};
        this.fields = data;

        const w = this.fields.weather == null ? null : JSON.parse(this.fields.weather)
        if(w != null){
          this.weather = w
        }

        this.line = this.fields.linhas;

        this.line.forEach(element => {
          element.ho = this.formatNumber(element.ho);
          element.hs = this.formatNumber(element.hs);
          element.ha = this.formatNumber(element.ha);
          element.kmi = this.formatNumber(element.kmi);
          element.km = this.formatNumber(element.km);
          element.hi = this.formatNumber(element.hi);
          element.h = this.formatNumber(element.h);
          element.hn = this.formatNumber(element.hn);
          element.hext = this.formatNumber(element.hext);
          element.diesel = this.formatNumber(element.diesel);
          element.oil = this.formatNumber(element.oil);
          element.adblue = this.formatNumber(element.adblue);

          if(element.ho == "NaN") element.ho = 0
          if(element.hs == "NaN") element.hs = 0
          if(element.ha == "NaN") element.ha = 0
          if(element.kmi == "NaN") element.km = 0
          if(element.km == "NaN") element.km = 0
          if(element.h == "NaN") element.h = 0
          if(element.hn == "NaN") element.hn = 0
          if(element.hext == "NaN") element.hext = 0
          if(element.diesel == "NaN") element.diesel = 0
          if(element.oil == "NaN") element.oil = 0
          if(element.adblue == "NaN") element.adblue = 0
        });

        this.loadingContent = 0

        this.changeWork()

        if(this.fields.frente_id == null)
          this.hideFrentes = true

        // As classes da frente (ou da obra sem frentes) sao carregadas em changeWork.
        // Nao chamar classesPorObra aqui: poderia substituir as opcoes da frente no update.

        this.showContent = true
      }
    },
  },
  data(){
    return{
      weather: null,
      originalCodification: {
        Data: null,
        frente_id: null,
        classe_id: null
      },
      menu2: false,
      classeNeeded: false,
      confirmationModal: {
        show: false,
        title: '',
        message: ''
      },
      hextDialog: {
        show: false
      },
      alert:{
        showAlert: false,
        message: ''
      },
      loadingContent: 2,
      hideFrentes: false,
      error: false,
      timeout: 2000,
      error_message: "",
      showContent: false,
      editorModal:false,
      success: false,
      savedSuccess: false,
      loader: false,
      fields:{
        obra_id: null,
        frente_id: null,
        classe_id: null,
        Data: null
      },
      deletedLines: [],
      line: [
      ],
      obras: [],
      equipamentos: [],
      unassignedMachines: [],
      classes:[],
      frentes_obra: [],
      inputEditorModalData:{
        itemModel:null,
        id: null,
      },
      rules_error_messages:{
        confirmation_message: 'Preencheu o formulario e está prestes a submeter o mesmo. \nTem a certeza que preencheu tudo bem? \n\nPretende prosseguir?',
        rule0: 'Há campos por preencher. Campos obrigatórios: HS, HO, HA, KM, H, HN e HExt',
        rule1: 'Horas normais + Horas extra devem ser superiores ou iguais a zero',
        rule2: 'As horas normais do equipamento não devem ser superiores ás horas de trabalho',
        rule3: 'Horas extra devem ser sempre iguais ou superiores às da linha anterior',
        rule4: 'Horas normais devem ser inferiores a 24',
        rule5: 'Campo dos KM ou Horas deve ser preenchido',
        rule6: 'Tem valores a 0 (zero). \n\nTenha atenção e confira se pretende mesmo que estes dados sejam registados. \n\nPretende prosseguir?'
      }
    }
  },
  mounted(){
    this.fillBaseData()
  },
  methods:{
    getText(ob) {
      if (ob.Descricao && ob.SubEmp && ob.Descricao.includes(ob.SubEmp)) {
        return ob.Descricao;
      }
      return `${ob.SubEmp} - ${ob.Descricao}`;
    },
    openNativeDatePicker() {
      if(this.method == 'update' && (!this.canRecode || this.approvedDiary))
        return

      const dateInput = this.$refs.dateInput
      if(!dateInput || !dateInput.$el)
        return

      const input = dateInput.$el.querySelector('input');
      if (input && typeof input.showPicker === 'function') {
        input.showPicker();
      }
    },

    normalizeCodificationValue(value) {
      if(value === undefined || value === null || value === '')
        return null

      return String(value)
    },

    hasRecodeChanges() {
      if(this.method !== 'update')
        return false

      return (
        this.normalizeCodificationValue(this.originalCodification.Data) !==
          this.normalizeCodificationValue(this.fields.Data) ||
        this.normalizeCodificationValue(this.originalCodification.frente_id) !==
          this.normalizeCodificationValue(this.fields.frente_id) ||
        this.normalizeCodificationValue(this.originalCodification.classe_id) !==
          this.normalizeCodificationValue(this.fields.classe_id)
      )
    },

    recodeDateChanged() {
      if(this.method !== 'update' || !this.canRecode || this.approvedDiary)
        return

      this.changeWork()
    },
    maxDate() {
      return new Date().toISOString().split("T")[0];
    },
    formatNumber(value) {
      if (isNaN(value)) return 0;
      let num = parseFloat(value);
      return Number.isInteger(num) || num.toFixed(2).endsWith('.00') ? num.toFixed(0) : num.toFixed(2);
    },
    onSuccess(){
      this.$router.push("/diarios/v2");
    },
    dataChanged(){
      if(this.obras.length == 1){
        this.fields.obra_id = this.obras[0].Id
        this.changeWork()
      }
    },
    checkNew(item){
      let aux = '' + item.id
      return aux.includes('new')
    },
    getVehicleName(item){
      return item.EquiEntityKey + ' - ' + item.EquipDescricao
    },
    disableBox(item){
      if(this.method == 'create') return false
      if(item.id != undefined)
        if(item.id.includes('new'))
          return false
      return true
    },
    deleteLine(item_object){
      let id = item_object.id

      if(item_object.accessory == false){
        let contador_de_maquinas = 0
        this.line.forEach(element => {
          if(element.accessory == false)
            contador_de_maquinas++
        });

        if(contador_de_maquinas == 1){
          this.error_message = "Tem de ter, SEMPRE, pelo menos uma linha de equipamento"
          this.error = true
          return
        }

        if(this.line.length == 1){
          this.error_message = "Não pode apagar a única linha que tem"
          this.error = true
          return
        }
      }

      let index = this.line.findIndex(item => item.id == id);
      this.line.splice(index, 1);

      if(!id.includes('new'))
        this.deletedLines.push(id)
    },

    // ✅ Preenche HExt=0 apenas quando o utilizador confirma
    confirmHextAndSubmit(){
      this.hextDialog.show = false

      // preencher automaticamente os hext null (apenas nas linhas não-acessório)
      this.line.forEach(l => {
        if(l.accessory === false && (l.hext === null || l.hext === '')){
          l.hext = 0
        }
      })

      // submeter diretamente
      this.createPD()
    },

    async createPD(){
      this.loader = true;
      this.confirmationModal.show = false

      try {
        let lineItems = []
        let i = 1;

        this.line.forEach(l => {
          let variavel = null

          if (typeof l.equip === 'string') {
            variavel = l.equip.trim()

            if(!l.accessory){
              this.getEquipamentos.forEach(element => {
                if(element.EntityKey == variavel)
                  variavel = element.Id
              });
            }else{
              this.unassignedMachines.forEach(element => {
                if(element.EntityKey == variavel)
                  variavel = element.Id
              });
            }
          }else{
            variavel = l.equip.Id
          }

          lineItems.push({
            IdEquipamento: variavel,
            accessory: l.accessory,
            NumLinha: i,
            local: l.local,
            Servico: l.hs,
            Ordem: l.ho,
            Avariado: l.ha,
            Contador2: l.km,
            HoraInicial: l.hi,
            KmInicial: l.kmi,
            Contador1: l.h,
            HNormal: l.hn,
            HExtra: l.hext,
            diesel: l.diesel,
            oil: l.oil,
            adblue: l.adblue,
            lineID: l.id
          })

          i++
        });

        let frenteObra = null
        this.frentes_obra.forEach(element => {
          if(element.ID == this.fields.frente_id)
            frenteObra = element
        });

        let obra = null
        this.obras.forEach(element => {
          if(element.Id == this.fields.obra_id)
            obra = element
        })

        if(obra == null){
          this.error_message = 'Não foi possível identificar a obra.'
          this.error = true
          this.loader = false
          return
        }

        let classe = null
        this.classes.forEach(element => {
          if(element.ID == this.fields.classe_id)
            classe = element
        });

        let data = {
          SubEmpId: frenteObra == null ? null : frenteObra.SubEmpId,
          classeID: frenteObra == null ? null : frenteObra.SubEmp,
          id_da_classe: this.fields.classe_id,
          hash: this.method == 'create' ? null : this.$route.params.hash,
          Codigo: obra.Codigo,
          Data: this.fields.Data,
          line: lineItems,
          classeID_manual: null,
          deletedLines: this.deletedLines,
          observations: this.fields.observations,
          id_user_creator: this.$route.params.id_user == undefined ? null : this.$route.params.id_user
        };

        if(data.classeID == null){
          if(this.fields.classe_id == null){
            this.error_message = 'Esta obra não tem uma classe associada. Para criar a diária é preciso ter, no mínimo, uma classe a ela associada'
            this.timeout = 10000
            this.error = true
            this.loader = false
            return
          }

          if(classe == null){
            this.error_message = 'Não foi possível identificar a classe selecionada.'
            this.timeout = 5000
            this.error = true
            this.loader = false
            return
          }

          data.classeID_manual = classe.ClasseId
        }

        if(this.method == 'create'){
          const resp = await PDEq.create(data)
          const response = resp.data

          if(response.success == false){
            this.error_message = response.error
            this.error = true
            this.timeout = 5000
            this.loader = false
            return
          }

          this.savedSuccess = true;
          this.error = false;
          this.loader = false;
          return
        }

        const needsRecode = this.hasRecodeChanges()

        if(needsRecode && !this.canRecode){
          this.error_message = 'Não tem permissão para alterar a data, frente ou classe desta parte diária.'
          this.error = true
          this.timeout = 5000
          this.loader = false
          return
        }

        if(!needsRecode){
          const resp = await PDEq.updatePD(data, this.$route.params.hash)
          const response = resp.data

          if(response.success == false){
            this.error_message = response.error
            this.error = true
            this.timeout = 5000
            this.loader = false
            return
          }

          this.savedSuccess = true;
          this.error = false;
          this.loader = false;
          return
        }

        const recodePayload = {
          id_user_creator: this.$route.params.id_user == undefined ? null : this.$route.params.id_user,
          Data: this.fields.Data,
          SubEmpId: frenteObra == null ? null : frenteObra.SubEmpId,
          id_da_classe: this.fields.classe_id,
          classeID_manual: data.classeID_manual
        }

        const recodeResp = await PDEq.recodePD(
          recodePayload,
          this.$route.params.hash
        )

        if(recodeResp.data && recodeResp.data.success == false){
          this.error_message = recodeResp.data.error || 'Não foi possível recodificar a parte diária.'
          this.error = true
          this.timeout = 5000
          this.loader = false
          return
        }

        const newHash = recodeResp.data.hash || this.$route.params.hash

        data.hash = newHash

        const updateResp = await PDEq.updatePD(data, newHash)

        if(updateResp.data && updateResp.data.success == false){
          this.error_message = updateResp.data.error || 'A parte diária foi recodificada, mas ocorreu um erro ao atualizar os restantes dados.'
          this.error = true
          this.timeout = 10000
          this.loader = false
          return
        }

        this.originalCodification = {
          Data: this.fields.Data,
          frente_id: this.fields.frente_id,
          classe_id: this.fields.classe_id
        }

        this.savedSuccess = true;
        this.error = false;
        this.loader = false;

      } catch(err) {
        if(err.response && err.response.status == 422){
          if(
            err.response.data &&
            err.response.data.errors &&
            this.$refs.form &&
            typeof this.$refs.form.setErrors === 'function'
          ){
            this.$refs.form.setErrors(err.response.data.errors);
          }
        }

        this.success = false;
        this.loader = false
        this.error = true;
        this.error_message =
          err.response?.data?.message ||
          err.response?.data?.error ||
          'Ocorreu um erro ao gravar a parte diária.';
        this.loading = false;
      }
    },

    createPD_validator(){
      let validation = this.validateFormData()

      // ✅ NOVA REGRA: se só o HExt estiver null, mostrar dialog e não continuar
      if(validation.only_hext_null){
        this.hextDialog.show = true
        return
      }

      if(validation.has_error){
        this.alert.showAlert = true
        setTimeout(() => {
          this.alert.showAlert = false
        }, "10000")
        return
      }

      if(validation.has_valores_zero){
        this.confirmationModal.show = true
        this.confirmationModal.title = 'Tem a certeza?'
        this.confirmationModal.message = this.rules_error_messages.rule6
      }else{
        this.confirmationModal.show = true
        this.confirmationModal.title = 'Tem a certeza?'
        this.confirmationModal.message = this.rules_error_messages.confirmation_message
      }
    },

    // ✅ ALTERADO: deteta caso "só HExt null" (sem outros campos em falta)
    validateFormData(){
      let last_lower_value = 0
      let has_error = false
      let line = 1
      this.alert.message = ''
      let has_valores_zero = false

      let only_hext_null = false
      let found_only_hext_null_candidate = false

      this.line.forEach(l => {
        if(l.accessory == false){
          let stepper = []

          // Verificar se há valores a zero
          if(
            l.km == 0 ||
            l.ho == 0 ||
            l.hs == 0 ||
            l.ha == 0 ||
            l.h == 0  ||
            l.hn == 0 ||
            l.hext == 0 ||
            l.diesel == 0
          ){
            has_valores_zero = true
          }

          const hoStr = l.ho != null ? l.ho.toString() : null
          const hsStr = l.hs != null ? l.hs.toString() : null
          const haStr = l.ha != null ? l.ha.toString() : null
          const hStr  = l.h  != null ? l.h.toString()  : null
          const hnStr = l.hn != null ? l.hn.toString() : null
          const hextStr = l.hext != null ? l.hext.toString() : null
          const dieselStr = l.diesel != null ? l.diesel.toString() : null
          const kmStr = l.km != null ? l.km.toString() : null

          // campos obrigatórios (para esta validação)
          const missing_ho = (hoStr === '' || hoStr == null)
          const missing_hs = (hsStr === '' || hsStr == null)
          const missing_ha = (haStr === '' || haStr == null)
          const missing_h  = (hStr  === '' || hStr  == null)
          const missing_hn = (hnStr === '' || hnStr == null)
          const missing_hext = (hextStr === '' || hextStr == null)
          const missing_diesel = (dieselStr === '' || dieselStr == null)

          // ✅ Caso especial: só HExt em falta
          const only_hext_missing =
            !missing_ho &&
            !missing_hs &&
            !missing_ha &&
            !missing_h &&
            !missing_hn &&
            !missing_diesel &&
            missing_hext

          if(only_hext_missing){
            found_only_hext_null_candidate = true
          }

          // Rule N# 0 - Campos por preencher
          // ✅ Mantém erro normal, EXCETO quando é só HExt em falta (nesse caso vamos abrir dialog)
          if(
            missing_ho ||
            missing_hs ||
            missing_ha ||
            missing_h  ||
            missing_hn ||
            missing_diesel ||
            (missing_hext && !only_hext_missing)
          ){
            stepper.push(1)
            has_error = true
            this.alert.message += "[Linha " + line + "] - " + this.rules_error_messages.rule0 + "\n\n";
          }else{
            stepper.push(0)
          }

          // se há erro noutra coisa, não queremos acionar "only_hext_null"
          // (porque já não é “só hext”)
          if(has_error){
            // noop
          } else {
            // se até aqui não há erro e esta linha é only_hext_missing, candidato continua
          }

          // Converter para float (quando existir)
          const ho = hoStr != null && hoStr !== '' ? parseFloat(hoStr) : null
          const hs = hsStr != null && hsStr !== '' ? parseFloat(hsStr) : null
          const ha = haStr != null && haStr !== '' ? parseFloat(haStr) : null
          const h  = hStr  != null && hStr  !== '' ? parseFloat(hStr)  : null
          const hn = hnStr != null && hnStr !== '' ? parseFloat(hnStr) : null
          const hext = hextStr != null && hextStr !== '' ? parseFloat(hextStr) : null
          const diesel = dieselStr != null && dieselStr !== '' ? parseFloat(dieselStr) : null

          // Rule N# 1 - HN+HExt >= 0 (só quando ambos existem)
          if(!l.accessory && hn != null && hext != null){
            //  BLOQUEIO: HN e HExt não podem ser ambos 0 na mesma linha
            if (hn === 0 && hext === 0) {
              has_error = true
              this.alert.message += `[Linha ${line}] - HN e HExt não podem ser ambos 0. Tem de existir horas normais ou extra.\n\n`
            }

            if((hn + hext) < 0){
              stepper.push(1)
              has_error = true
              this.alert.message += "[Linha " + line + "] - " + this.rules_error_messages.rule1 + "\n\n";
            }else{
              stepper.push(0)
            }

            // Rule N# 3 - HExt >= last
            if(hext < last_lower_value){
              stepper.push(1)
              has_error = true
              this.alert.message += "[Linha " + line + "] - " + this.rules_error_messages.rule3 + "\n\n";
            }else{
              stepper.push(0)
              last_lower_value = hext
            }
          }

          // Rule N# 4 - HN <= 40 (só quando existe)
          if(hn != null){
            if(hn > 40){
              stepper.push(1)
              has_error = true
              this.alert.message += "[Linha " + line + "] - " + this.rules_error_messages.rule4 + "\n\n";
            }else{
              stepper.push(0)
            }
          }

          // Rule N# 5 - KM preenchido
          if(kmStr == '' || kmStr == null){
            stepper.push(1)
            has_error = true
            this.alert.message += "[Linha " + line + "] - " + this.rules_error_messages.rule5 + "\n\n";
          }else{
            stepper.push(0)
          }

          line++
        }
      });

      // ✅ Só abre o dialog se:
      // - existe pelo menos um caso only_hext_missing
      // - e não há mais erros nenhuns
      // (senão deixa seguir com os erros normais)
      if(found_only_hext_null_candidate && !has_error){
        only_hext_null = true
      }

      return {
        has_error: has_error,
        has_valores_zero: has_valores_zero,
        only_hext_null: only_hext_null
      }
    },

    async changeFrenteObra(options = {}) {
      const preserveSelection = options != null && typeof options === 'object' && options.preserveSelection === true
      if (this.fields.obra_id == null) return

      const frente = Array.isArray(this.frentes_obra)
        ? this.frentes_obra.find(f => f.ID == this.fields.frente_id)
        : null

      if (!frente) {
        this.classes = []
        this.classeNeeded = false
        if (!preserveSelection) this.fields.classe_id = null
        return
      }

      const obra = Array.isArray(this.obras)
        ? this.obras.find(o => o.Id == this.fields.obra_id)
        : null
      const obraCodigo = obra && obra.Codigo ? obra.Codigo : null
      const frenteSelecionada = this.fields.frente_id
      const obraSelecionada = this.fields.obra_id

      if (!preserveSelection) this.fields.classe_id = null

      if (!obraCodigo || frente.SubEmpId == null) {
        console.error('Nao foi possivel identificar obra ou frente para carregar classes', { obraCodigo, frente })
        this.classes = []
        this.classeNeeded = false
        return
      }

      try {
        // Usar o endpoint de classes da frente, com o codigo da obra obrigatorio.
        // O backend devolve as classes gerais quando a frente nao tem classes especificas.
        const resp = await FrenteObra.getFrenteObraClasses(frente.SubEmpId, obraCodigo)
        if (this.fields.frente_id != frenteSelecionada || this.fields.obra_id != obraSelecionada) return

        const classesRecebidas = resp && Array.isArray(resp.data) ? resp.data : []
        this.classes = classesRecebidas
        this.classeNeeded = classesRecebidas.length > 0

        if (this.classes.length === 1 && this.fields.classe_id == null) {
          this.fields.classe_id = this.classes[0].ID
          this.changeClasse()
        }
      } catch (error) {
        if (this.fields.frente_id != frenteSelecionada || this.fields.obra_id != obraSelecionada) return
        console.error('Erro ao carregar classes da frente:', error)
        // Nao apagar a classe previamente selecionada se for um erro temporario.
        if (!preserveSelection) {
          this.classes = []
          this.classeNeeded = false
        }
      }
    },
    getDiaryUserMeta () {
      // 1) preferir última linha de máquina (accessory=false) com CodigoMO
      for (let i = this.line.length - 1; i >= 0; i--) {
        const l = this.line[i]
        if (l && l.accessory === false && l.CodigoMO != null) {
          return { CodigoMO: l.CodigoMO, name: l.name }
        }
      }

      // 2) fallback: qualquer linha com CodigoMO
      for (let i = this.line.length - 1; i >= 0; i--) {
        const l = this.line[i]
        if (l && l.CodigoMO != null) {
          return { CodigoMO: l.CodigoMO, name: l.name }
        }
      }

      return { CodigoMO: null, name: null }
    },
    addAccessoryLine () {
      const lastLine = this.line[this.line.length - 1]
      const f = this.$store.state.user.data.funcionario

      let meta = { CodigoMO: null, name: null }

      if (this.method === 'create') {
        meta = f ? { CodigoMO: f.Codigo, name: f.Nome } : meta
      } else {
        meta = this.getDiaryUserMeta()
      }

      this.line.push({
        id: 'new-' + dayjs().unix(),
        accessory: true,
        equip: null,

        ho: lastLine?.ho ?? 0,
        hs: lastLine?.hs ?? 0,
        ha: lastLine?.ha ?? 0,
        km: lastLine?.km ?? 0,
        kmi: lastLine?.kmi ?? 0,
        hi: lastLine?.hi ?? 0,
        h: lastLine?.h ?? 0,

        local: null,

        CodigoMO: meta.CodigoMO,
        name: meta.name,

        hn: null,
        hext: null,
        diesel: null,
        oil: null,
        adblue: null,
        options: ""
      })
    },
    addLineToTable () {
      const f = this.$store.state.user.data.funcionario

      let meta = { CodigoMO: null, name: null }

      if (this.method === 'create') {
        meta = f ? { CodigoMO: f.Codigo, name: f.Nome } : meta
      } else {
        // update: herdar da diária
        meta = this.getDiaryUserMeta()
      }

      this.line.push({
        id: 'new-' + dayjs().unix(),
        accessory: false,
        equip: null,

        CodigoMO: meta.CodigoMO,
        name: meta.name,

        ho: 0, hs: 0, ha: 0,
        km: 0, kmi: 0, hi: 0, h: 0,
        local: null,
        hn: 8,
        hext: null,
        diesel: 0,
        oil: 0,
        adblue: 0,
        options: ""
      })
    },
    changeEditorModal(e){
      this.line.forEach(element => {
        if(element.id == this.inputEditorModalData.id)
          element.local = e
      });
    },

    openEditorModal(item){
      this.inputEditorModalData.itemModel = item.local
      this.inputEditorModalData.id = item.id
      this.editorModal=true
    },

    async fillBaseData(){
      let data = { tipo: 'O' }
      await Obra.all(data).then((resp) =>  {
        this.obras = resp.data;
      });
    },

    async changeWork(){
      if (this.fields.obra_id == null) return

      // No update o watcher pode executar antes de mounted/fillBaseData.
      if (this.obras.length === 0) await this.fillBaseData()

      const obraId = this.fields.obra_id
      const data = {
        work_id: obraId,
        codigo: this.$store.state.user.data.funcionario.Codigo,
        data: this.fields.Data,
        place: 'manobrador'
      }

      const obra = this.obras.find(o => o.Id == obraId)
      const obraPai = obra ? obra.Codigo : null

      if (obraPai) {
        try {
          const resp = await FrenteObra.all(obraPai)
          if (this.fields.obra_id != obraId) return

          this.frentes_obra = Array.isArray(resp.data) ? resp.data : []
          if (this.frentes_obra.length === 0) {
            this.hideFrentes = true
            await this.classesPorObra()
          } else {
            this.hideFrentes = false
            if (this.frentes_obra.length === 1 && this.fields.frente_id == null)
              this.fields.frente_id = this.frentes_obra[0].ID

            if (this.fields.frente_id != null) {
              await this.changeFrenteObra({ preserveSelection: true })
            } else {
              this.classes = []
              this.classeNeeded = false
            }
          }
        } catch (error) {
          console.error('Erro ao carregar frentes da obra:', error)
        }
      }

      Obra.getObraEquipamentos(data).then((resp) => {
        this.equipamentos = resp.data.externo
        this.loadingContent++
      })

      Equipamentos.getUnassigned(obraId, 'OPERATOR').then((resp) => {
        this.unassignedMachines = resp.data.filter(item => item.is_accessory === '1')
        this.loadingContent++
      })

      if (this.weather == null && this.method == 'create') {
        Work.getWeather({ id: obraId, date: this.fields.Data }).then(resp => {
          this.weather = resp.data
        })
      }
    },

    async classesPorObra(){
      const resp = await Classe.getClassesByObraPai(this.fields.obra_id)
      this.classes = Array.isArray(resp.data) ? resp.data : []
      this.classeNeeded = this.classes.length > 0
      if (this.classes.length === 1 && this.fields.classe_id == null) {
        this.fields.classe_id = this.classes[0].ID
        this.changeClasse()
      }
    },

    changeClasse(){
      if(this.fields.obra_id == null) return
      if(this.fields.classe_id == null) return
      this.showContent = true
    }
  },
  computed:{
    canRecode(){
      if(this.$store.getters.hasRole && this.$store.getters.hasRole(['super']))
        return true

      if(
        this.$store.getters.hasPermission &&
        typeof this.$store.getters.hasPermission === 'function'
      ){
        return this.$store.getters.hasPermission(['diaries.recode'])
      }

      const user = this.$store.state.user && this.$store.state.user.data
        ? this.$store.state.user.data
        : null

      if(!user)
        return false

      const permissions = user.permissions || []

      return permissions.some(permission => {
        if(typeof permission === 'string')
          return permission === 'diaries.recode'

        return permission && permission.name === 'diaries.recode'
      })
    },

    weatherSummary () {
      if (!this.weather) return null
      let meta = null

      try {
        if (this.method === 'update') {
          if (this.weather.day) {
            meta = this.weather
          } else if (this.weather.metadata) {
            meta = typeof this.weather.metadata === 'string'
              ? JSON.parse(this.weather.metadata)
              : this.weather.metadata
          }
        } else {
          if (!this.weather.metadata) return null
          meta = typeof this.weather.metadata === 'string'
            ? JSON.parse(this.weather.metadata)
            : this.weather.metadata
        }
      } catch (e) {
        console.error('Erro ao fazer parse do metadata de weather:', e)
        return null
      }

      if (!meta || !meta.day) return null

      const day = meta.day
      const conditionText = day.condition?.text || ''
      const lower = conditionText.toLowerCase()
      let icon = 'mdi-weather-partly-cloudy'

      if (lower.includes('chuva') || lower.includes('chuvisco') || lower.includes('aguace')) {
        icon = 'mdi-weather-rainy'
      } else if (lower.includes('neve')) {
        icon = 'mdi-weather-snowy'
      } else if (lower.includes('neblina') || lower.includes('nevoeiro') || lower.includes('nevoa')) {
        icon = 'mdi-weather-fog'
      } else if (lower.includes('sol') || lower.includes('céu limpo') || lower.includes('ceu limpo')) {
        icon = 'mdi-weather-sunny'
      } else if (lower.includes('nublado') || lower.includes('encoberto')) {
        icon = 'mdi-weather-cloudy'
      }

      return {
        icon,
        conditionText,
        max: day.maxtemp_c,
        min: day.mintemp_c,
        wind: day.maxwind_kph,
      }
    },

    checkSaveButton(){
      let lock_save = false
      this.line.forEach(element => {
        if(element.equip == null)
          lock_save = true
      });
      return lock_save;
    },

    approvedDiary(){
      let returner = true
      if(this.method=='create') return false

      this.fields.linhas.forEach(element => {
        if(element.WebAppApproved == false)
          returner = false
      });

      return returner
    },

    disabledDiary(){
      let returner = true

      if(this.$store.getters.hasRole(['super', 'administrativo', 'engenheiro']))
        return false

      if(this.method=='create')
        return false

      this.fields.linhas.forEach(element => {
        if(element.WebAppApproved == false)
          returner = false
      });

      return returner
    },

    getLines(){
      return this.line
    },

    getEquipamentos(){
      let returner = []

      this.equipamentos.forEach(equipamento => {
        let existe = false
        this.line.forEach(linha => {
          if(linha.equip != null)
            if(equipamento.Id == linha.equip.Id)
              existe = true
        });
        if(existe == false)
          returner.push(equipamento)
      });

      return this.equipamentos
    }
  }
}
</script>

<style>
.v-text-field__slot label,input{
    font-size: 13px !important;
}

.v-list-item__title{
    font-size: 13px !important;
}

.v-select__slot label,input{
    font-size: 13px !important;
}
.bg-gray-100 {
    background-color: #f5f5f5; /* Cor cinza clara */
}
</style>
